import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  CpuChipIcon,
  ServerIcon,
  CloudIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'

import { api } from '../services/api'
import { Button } from '../components/Button'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { useAuth } from '../hooks/useAuth'

const packageTypeIcons = {
  media: '🎬',
  ai_corporate: '🤖',
  full_stack: '🏠',
  website_hosting: '🌐',
  hardware_bundle: '💻',
}

const packageTypeNames = {
  media: 'Media & Entertainment Package',
  ai_corporate: 'Corporate AI Solution',
  full_stack: 'Full Stack Self-Hosting',
  website_hosting: 'Website Hosting',
  hardware_bundle: 'Hardware Bundle',
}

export function PackageDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [selectedBilling, setSelectedBilling] = useState<'monthly' | 'yearly' | 'one_time'>('monthly')

  const {
    data: packageData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['package', slug],
    queryFn: () => api.packages.getPackageBySlug(slug!),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !packageData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Package Not Found</h1>
          <p className="text-gray-600 mb-6">
            The package you're looking for doesn't exist or has been removed.
          </p>
          <Button as={Link} to="/packages" variant="outline">
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Packages
          </Button>
        </div>
      </div>
    )
  }

  const icon = packageTypeIcons[packageData.package_type] || '📦'
  const typeName = packageTypeNames[packageData.package_type] || packageData.package_type

  const getPriceInfo = () => {
    const monthlyPrice = parseFloat(packageData.monthly_price)
    const yearlyPrice = parseFloat(packageData.yearly_price)
    const oneTimePrice = packageData.one_time_price ? parseFloat(packageData.one_time_price) : 0

    switch (selectedBilling) {
      case 'monthly':
        return monthlyPrice > 0 ? {
          price: `$${monthlyPrice.toFixed(2)}`,
          period: '/month',
          savings: null
        } : null
      case 'yearly':
        return yearlyPrice > 0 ? {
          price: `$${yearlyPrice.toFixed(2)}`,
          period: '/year',
          savings: monthlyPrice > 0 ?
            `Save $${((monthlyPrice * 12) - yearlyPrice).toFixed(2)}` : null
        } : null
      case 'one_time':
        return oneTimePrice > 0 ? {
          price: `$${oneTimePrice.toFixed(2)}`,
          period: '',
          savings: null
        } : null
      default:
        return null
    }
  }

  const priceInfo = getPriceInfo()

  const handlePurchase = () => {
    if (!isAuthenticated) {
      // Redirect to login or show login modal
      navigate('/login?redirect=' + encodeURIComponent(window.location.pathname))
      return
    }

    // Navigate to checkout with selected billing type
    navigate(`/checkout?package=${packageData.slug}&billing=${selectedBilling}`)
  }

  return (
    <motion.div
      className="min-h-screen bg-gray-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center">
            <Button
              as={Link}
              to="/packages"
              variant="ghost"
              className="mr-4"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back to Packages
            </Button>
            <div className="flex items-center">
              <span className="text-2xl mr-3">{icon}</span>
              <div>
                <p className="text-sm font-medium text-primary-600">{typeName}</p>
                <h1 className="text-2xl font-bold text-gray-900">{packageData.name}</h1>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Package Image/Hero */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {packageData.image_url ? (
                <img
                  src={packageData.image_url}
                  alt={packageData.name}
                  className="w-full h-64 object-cover"
                />
              ) : (
                <div className="w-full h-64 bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                  <span className="text-8xl">{icon}</span>
                </div>
              )}
              {packageData.badge_text && (
                <div className="absolute top-4 right-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-600 text-white">
                    {packageData.badge_text}
                  </span>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">About This Package</h2>
              <p className="text-gray-700 leading-relaxed">{packageData.description}</p>
            </div>

            {/* Features */}
            {packageData.features && packageData.features.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">What's Included</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {packageData.features.map((feature, index) => (
                    <div
                      key={index}
                      className={`flex items-start p-4 rounded-lg ${
                        feature.highlighted ? 'bg-primary-50 border border-primary-200' : 'bg-gray-50'
                      }`}
                    >
                      {feature.included ? (
                        <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XMarkIcon className="w-5 h-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <h3 className={`font-medium ${feature.highlighted ? 'text-primary-900' : 'text-gray-900'}`}>
                          {feature.title}
                        </h3>
                        <p className={`text-sm ${feature.highlighted ? 'text-primary-700' : 'text-gray-600'} mt-1`}>
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Technical Specs */}
            {packageData.technical_specs && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Technical Specifications</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {packageData.technical_specs.cpu_cores && (
                    <div className="text-center">
                      <CpuChipIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                      <div className="text-lg font-semibold text-gray-900">
                        {packageData.technical_specs.cpu_cores}
                      </div>
                      <div className="text-sm text-gray-600">CPU Cores</div>
                    </div>
                  )}
                  {packageData.technical_specs.ram_gb && (
                    <div className="text-center">
                      <ServerIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                      <div className="text-lg font-semibold text-gray-900">
                        {packageData.technical_specs.ram_gb}GB
                      </div>
                      <div className="text-sm text-gray-600">RAM</div>
                    </div>
                  )}
                  {packageData.technical_specs.storage_gb && (
                    <div className="text-center">
                      <CloudIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                      <div className="text-lg font-semibold text-gray-900">
                        {packageData.technical_specs.storage_gb}GB
                      </div>
                      <div className="text-sm text-gray-600">Storage</div>
                    </div>
                  )}
                  {packageData.technical_specs.ssl_included && (
                    <div className="text-center">
                      <ShieldCheckIcon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
                      <div className="text-lg font-semibold text-green-600">Included</div>
                      <div className="text-sm text-gray-600">SSL Certificate</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Google Workspace Info */}
            {packageData.includes_google_workspace && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h2 className="text-xl font-bold text-blue-900 mb-4">Google Workspace Included</h2>
                <p className="text-blue-700 mb-4">
                  This package includes Google Workspace {packageData.google_workspace_tier} edition
                  with professional email, calendar, drive storage, and collaboration tools.
                </p>
                <div className="flex items-center text-blue-600">
                  <CheckIcon className="w-5 h-5 mr-2" />
                  <span className="font-medium">Professional email setup included</span>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Pricing & Purchase */}
          <div className="lg:col-span-1">
            <div className="sticky top-8 space-y-6">
              {/* Pricing Card */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Choose Your Plan</h3>

                {/* Billing Options */}
                <div className="space-y-3 mb-6">
                  {parseFloat(packageData.monthly_price) > 0 && (
                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="billing"
                        value="monthly"
                        checked={selectedBilling === 'monthly'}
                        onChange={(e) => setSelectedBilling(e.target.value as 'monthly')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Monthly</span>
                          <span className="font-bold">${parseFloat(packageData.monthly_price).toFixed(2)}/mo</span>
                        </div>
                      </div>
                    </label>
                  )}

                  {parseFloat(packageData.yearly_price) > 0 && (
                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="billing"
                        value="yearly"
                        checked={selectedBilling === 'yearly'}
                        onChange={(e) => setSelectedBilling(e.target.value as 'yearly')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Yearly</span>
                          <span className="font-bold">${parseFloat(packageData.yearly_price).toFixed(2)}/yr</span>
                        </div>
                        {parseFloat(packageData.monthly_price) > 0 && (
                          <div className="text-sm text-green-600">
                            Save ${((parseFloat(packageData.monthly_price) * 12) - parseFloat(packageData.yearly_price)).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </label>
                  )}

                  {packageData.one_time_price && parseFloat(packageData.one_time_price) > 0 && (
                    <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="billing"
                        value="one_time"
                        checked={selectedBilling === 'one_time'}
                        onChange={(e) => setSelectedBilling(e.target.value as 'one_time')}
                        className="text-primary-600 focus:ring-primary-500"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">One-time</span>
                          <span className="font-bold">${parseFloat(packageData.one_time_price).toFixed(2)}</span>
                        </div>
                      </div>
                    </label>
                  )}
                </div>

                {/* Selected Price Display */}
                {priceInfo && (
                  <div className="border-t pt-4 mb-6">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-gray-900">
                        {priceInfo.price}
                      </span>
                      {priceInfo.period && (
                        <span className="text-gray-500 ml-1">{priceInfo.period}</span>
                      )}
                    </div>
                    {priceInfo.savings && (
                      <div className="text-sm text-green-600 font-medium">
                        {priceInfo.savings}
                      </div>
                    )}
                    {parseFloat(packageData.setup_fee) > 0 && (
                      <div className="text-sm text-gray-500 mt-1">
                        + ${parseFloat(packageData.setup_fee).toFixed(2)} setup fee
                      </div>
                    )}
                  </div>
                )}

                {/* Purchase Button */}
                <Button
                  onClick={handlePurchase}
                  className="w-full mb-4"
                  size="lg"
                  disabled={!priceInfo}
                >
                  {isAuthenticated ? 'Get Started' : 'Sign In to Purchase'}
                </Button>

                {/* Contact for Custom */}
                <Button
                  as={Link}
                  to="/contact"
                  variant="outline"
                  className="w-full"
                >
                  Contact for Custom Quote
                </Button>
              </div>

              {/* Guarantee */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-900 mb-2">30-Day Money Back Guarantee</h4>
                <p className="text-green-700 text-sm">
                  Not satisfied? Get a full refund within 30 days, no questions asked.
                </p>
              </div>

              {/* Support */}
              <div className="bg-primary-50 border border-primary-200 rounded-xl p-6">
                <h4 className="font-bold text-primary-900 mb-2">Expert Support Included</h4>
                <p className="text-primary-700 text-sm">
                  Professional setup, 24/7 monitoring, and ongoing technical support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}